if (!customElements.get('testimonial-split-slider')) {
  class TestimonialSplitSlider extends HTMLElement {
    constructor() {
      super();

      this.currentIndex = 0;
      this.isTransitioning = false;
      this.pendingLoopJump = null;
      this.touchStartX = 0;
      this.touchDeltaX = 0;
      this.autoplayTimer = null;
      this.lastAdvanceTime = 0;
      this.transitionFallbackTimer = null;
      this.intersectionObserver = null;
      this.isInView = false;
      this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.handlePrevClick = this.handlePrevClick.bind(this);
      this.handleNextClick = this.handleNextClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleTouchStart = this.handleTouchStart.bind(this);
      this.handleTouchMove = this.handleTouchMove.bind(this);
      this.handleTouchEnd = this.handleTouchEnd.bind(this);
      this.handleTouchCancel = this.handleTouchCancel.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleTrackTransitionEnd = this.handleTrackTransitionEnd.bind(this);
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
      this.handleIntersection = this.handleIntersection.bind(this);
    }

    connectedCallback() {
      this.viewport = this.querySelector('[data-slider-viewport]');
      this.track = this.querySelector('[data-slider-track]');
      this.mediaViewport = this.querySelector('[data-media-viewport]');
      this.mediaTrack = this.querySelector('[data-media-track]');
      this.removeLoopClones(this.track);
      this.removeLoopClones(this.mediaTrack);
      this.slides = this.getTrackSlides(this.track, 'data-slide-index');
      this.mediaSlides = this.getTrackSlides(this.mediaTrack, 'data-media-slide-index');
      this.prevButton = this.querySelector('[data-slider-prev]');
      this.nextButton = this.querySelector('[data-slider-next]');
      this.pagination = this.querySelector('[data-slider-pagination]');
      this.interactiveViewports = [this.viewport, this.mediaViewport].filter(Boolean);
      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.autoplayInterval = Number(this.dataset.autoplayInterval) || 5000;

      if (!this.viewport || !this.track || this.slides.length === 0) {
        return;
      }

      this.stopAutoplay();
      this.clearTransitionFallback();
      this.detachEvents();
      this.viewport.setAttribute('aria-live', 'polite');
      this.setupLoopClones();
      this.setupPagination();
      this.attachEvents();
      this.classList.add('is-ready');
      this.goTo(0, false, true);
      this.observeVisibility();
      this.startAutoplay();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      this.unobserveVisibility();
      this.clearTransitionFallback();
      this.detachEvents();
      this.removeLoopClones(this.track);
      this.removeLoopClones(this.mediaTrack);
    }

    // ── Events ──────────────────────────────────────────────

    attachEvents() {
      if (this.prevButton) {
        this.prevButton.addEventListener('click', this.handlePrevClick);
      }

      if (this.nextButton) {
        this.nextButton.addEventListener('click', this.handleNextClick);
      }

      if (this.track) {
        this.track.addEventListener('transitionend', this.handleTrackTransitionEnd);
      }

      this.interactiveViewports.forEach(viewport => {
        viewport.addEventListener('keydown', this.handleKeydown);
        viewport.addEventListener('touchstart', this.handleTouchStart, { passive: true });
        viewport.addEventListener('touchmove', this.handleTouchMove, { passive: true });
        viewport.addEventListener('touchend', this.handleTouchEnd);
        viewport.addEventListener('touchcancel', this.handleTouchCancel);
      });

      document.addEventListener('visibilitychange', this.handleVisibilityChange);
      document.addEventListener('shopify:block:select', this.handleBlockSelect);
    }

    detachEvents() {
      if (this.prevButton) {
        this.prevButton.removeEventListener('click', this.handlePrevClick);
      }

      if (this.nextButton) {
        this.nextButton.removeEventListener('click', this.handleNextClick);
      }

      if (this.track) {
        this.track.removeEventListener('transitionend', this.handleTrackTransitionEnd);
      }

      (this.interactiveViewports || []).forEach(viewport => {
        viewport.removeEventListener('keydown', this.handleKeydown);
        viewport.removeEventListener('touchstart', this.handleTouchStart);
        viewport.removeEventListener('touchmove', this.handleTouchMove);
        viewport.removeEventListener('touchend', this.handleTouchEnd);
        viewport.removeEventListener('touchcancel', this.handleTouchCancel);
      });

      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      document.removeEventListener('shopify:block:select', this.handleBlockSelect);
    }

    // ── Autoplay ────────────────────────────────────────────

    /**
     * Checks live DOM state every tick — no paired events to
     * get out of sync. Uses :focus-visible so click-focus on
     * buttons/pagination doesn't permanently kill autoplay.
     */
    get isAutoplayPaused() {
      if (document.hidden) return true;
      if (this.prefersReducedMotion.matches) return true;
      if (!this.isInView) return true;

      return false;
    }

    /**
     * Gates autoplay on visibility: the interval clock only
     * advances while the section is in the viewport. Combined
     * with the watchdog (which pushes `lastAdvanceTime` forward
     * while paused), a full interval elapses from the moment the
     * section scrolls into view before the first auto-advance.
     */
    observeVisibility() {
      this.unobserveVisibility();

      if (!('IntersectionObserver' in window)) {
        this.isInView = true;
        return;
      }

      this.intersectionObserver = new IntersectionObserver(this.handleIntersection, {
        threshold: 0.2,
      });

      this.intersectionObserver.observe(this);
    }

    unobserveVisibility() {
      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
        this.intersectionObserver = null;
      }
    }

    handleIntersection(entries) {
      const entry = entries[0];

      if (!entry) {
        return;
      }

      this.isInView = entry.isIntersecting;
    }

    /**
     * Watchdog pattern: a 1-second polling loop that tracks
     * elapsed time since the last advance.
     *
     * - While paused, `lastAdvanceTime` is continuously pushed
     *   forward, so a FULL interval always elapses after
     *   un-pausing. No more unpredictable 0–5s waits.
     *
     * - Self-healing: no chain to break. Even if a tick is
     *   throttled or delayed, the next tick picks up cleanly.
     *
     * - goTo() resets `lastAdvanceTime` so manual navigation
     *   always gets a full interval before the next auto-advance.
     */
    startAutoplay() {
      this.stopAutoplay();

      if (!this.autoplayEnabled || this.slides.length < 2 || this.autoplayInterval <= 0) {
        return;
      }

      this.lastAdvanceTime = Date.now();

      this.autoplayTimer = window.setInterval(() => {
        if (this.isAutoplayPaused) {
          this.lastAdvanceTime = Date.now();
          return;
        }

        if (Date.now() - this.lastAdvanceTime >= this.autoplayInterval) {
          this.lastAdvanceTime = Date.now();

          if (this.isTransitioning) {
            this.completeTransition();
          }

          this.goTo(this.currentIndex + 1, false);
        }
      }, 250);
    }

    stopAutoplay() {
      if (this.autoplayTimer) {
        window.clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    // ── Transitions ─────────────────────────────────────────

    clearTransitionFallback() {
      if (this.transitionFallbackTimer) {
        window.clearTimeout(this.transitionFallbackTimer);
        this.transitionFallbackTimer = null;
      }
    }

    scheduleTransitionFallback() {
      this.clearTransitionFallback();

      if (!this.isTransitioning) {
        return;
      }

      this.transitionFallbackTimer = window.setTimeout(() => {
        this.completeTransition();
      }, 500);
    }

    completeTransition() {
      this.clearTransitionFallback();

      if (this.pendingLoopJump !== null) {
        const jumpTarget = this.pendingLoopJump;
        this.pendingLoopJump = null;
        this.jumpToDisplayIndex(jumpTarget);
      }

      this.isTransitioning = false;
    }

    handleTrackTransitionEnd(event) {
      if (event.target !== this.track || event.propertyName !== 'transform') {
        return;
      }

      this.completeTransition();
    }

    handleVisibilityChange() {
      if (!document.hidden) {
        this.completeTransition();
      }
    }

    // ── Track helpers ───────────────────────────────────────

    getTrackSlides(track, indexAttribute) {
      if (!track) {
        return [];
      }

      return Array.from(track.children).filter(slide => {
        return slide.hasAttribute(indexAttribute) && slide.dataset.loopClone !== 'true';
      });
    }

    removeLoopClones(track) {
      if (!track) {
        return;
      }

      Array.from(track.children).forEach(slide => {
        if (slide.dataset.loopClone === 'true') {
          slide.remove();
        }
      });
    }

    setupLoopClones() {
      if (this.slides.length > 1) {
        this.track.prepend(this.createLoopClone(this.slides[this.slides.length - 1]));
        this.track.append(this.createLoopClone(this.slides[0]));
      }

      if (this.mediaTrack && this.mediaSlides.length > 1) {
        this.mediaTrack.prepend(this.createLoopClone(this.mediaSlides[this.mediaSlides.length - 1]));
        this.mediaTrack.append(this.createLoopClone(this.mediaSlides[0]));
      }
    }

    createLoopClone(slide) {
      const clone = slide.cloneNode(true);
      clone.dataset.loopClone = 'true';
      clone.setAttribute('aria-hidden', 'true');

      return clone;
    }

    getDisplayIndex(index) {
      return this.slides.length > 1 ? index + 1 : index;
    }

    setTransitionEnabled(enabled) {
      const transitionValue = enabled ? '' : 'none';

      if (this.track) {
        this.track.style.transition = transitionValue;
      }

      if (this.mediaTrack) {
        this.mediaTrack.style.transition = transitionValue;
      }
    }

    setTrackPosition(displayIndex) {
      if (this.track) {
        this.track.style.transform = `translate3d(-${displayIndex * 100}%, 0, 0)`;
      }

      if (this.mediaTrack) {
        this.mediaTrack.style.transform = `translate3d(-${displayIndex * 100}%, 0, 0)`;
      }
    }

    jumpToDisplayIndex(displayIndex) {
      this.setTransitionEnabled(false);
      this.setTrackPosition(displayIndex);

      if (this.track) {
        this.track.offsetHeight;
      }

      if (this.mediaTrack) {
        this.mediaTrack.offsetHeight;
      }

      this.setTransitionEnabled(true);
    }

    // ── Pagination ──────────────────────────────────────────

    setupPagination() {
      if (!this.pagination) {
        return;
      }

      this.pagination.innerHTML = '';

      this.slides.forEach((slide, index) => {
        slide.setAttribute('aria-label', `${index + 1} of ${this.slides.length}`);
        slide.setAttribute('aria-hidden', index === this.currentIndex ? 'false' : 'true');

        const bullet = document.createElement('button');
        bullet.type = 'button';
        bullet.className = 'testimonial-split__pagination-button';
        bullet.setAttribute('aria-label', `Go to review ${index + 1}`);
        bullet.addEventListener('click', () => this.goTo(index));
        this.pagination.appendChild(bullet);
      });
    }

    // ── User interaction handlers ───────────────────────────

    handlePrevClick() {
      if (this.isTransitioning) {
        return;
      }
      this.lastAdvanceTime = Date.now();
      this.goTo(this.currentIndex - 1);
    }

    handleNextClick() {
      if (this.isTransitioning) {
        return;
      }
      this.lastAdvanceTime = Date.now();
      this.goTo(this.currentIndex + 1);
    }

    handleKeydown(event) {
      if (this.slides.length < 2) {
        return;
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.handlePrevClick();
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.handleNextClick();
      }
    }

    handleTouchStart(event) {
      if (this.slides.length < 2) {
        return;
      }

      this.touchStartX = event.changedTouches[0].clientX;
      this.touchDeltaX = 0;
    }

    handleTouchMove(event) {
      if (this.slides.length < 2) {
        return;
      }

      this.touchDeltaX = event.changedTouches[0].clientX - this.touchStartX;
    }

    handleTouchEnd() {
      if (this.slides.length < 2 || Math.abs(this.touchDeltaX) < 40) {
        return;
      }

      if (this.touchDeltaX > 0) {
        this.handlePrevClick();
      } else {
        this.handleNextClick();
      }
    }

    handleTouchCancel() {
      this.touchDeltaX = 0;
    }

    handleBlockSelect(event) {
      const selectedSlide = event.target.closest('[data-slide-index]');

      if (!selectedSlide || !this.contains(selectedSlide)) {
        return;
      }

      const selectedIndex = Number(selectedSlide.dataset.slideIndex);

      if (!Number.isNaN(selectedIndex)) {
        this.goTo(selectedIndex, false);
      }
    }

    // ── Navigation ──────────────────────────────────────────

    goTo(index, shouldFocus = true, immediate = false) {
      if (this.slides.length === 0) {
        return;
      }

      const slideCount = this.slides.length;
      const nextIndex = ((index % slideCount) + slideCount) % slideCount;
      const shouldLoopForward = slideCount > 1 && index >= slideCount;
      const shouldLoopBackward = slideCount > 1 && index < 0;

      this.clearTransitionFallback();
      this.currentIndex = nextIndex;
      this.pendingLoopJump = null;
      this.isTransitioning = !immediate && slideCount > 1;

      if (immediate) {
        this.jumpToDisplayIndex(this.getDisplayIndex(nextIndex));
      } else if (shouldLoopForward) {
        this.pendingLoopJump = this.getDisplayIndex(nextIndex);
        this.setTrackPosition(slideCount + 1);
      } else if (shouldLoopBackward) {
        this.pendingLoopJump = this.getDisplayIndex(nextIndex);
        this.setTrackPosition(0);
      } else {
        this.setTrackPosition(this.getDisplayIndex(nextIndex));
      }

      if (this.isTransitioning) {
        this.scheduleTransitionFallback();
      }

      this.slides.forEach((slide, slideIndex) => {
        slide.setAttribute('aria-hidden', slideIndex === nextIndex ? 'false' : 'true');
      });

      this.mediaSlides.forEach((slide, slideIndex) => {
        slide.setAttribute('aria-hidden', slideIndex === nextIndex ? 'false' : 'true');
      });

      if (this.pagination) {
        Array.from(this.pagination.children).forEach((bullet, bulletIndex) => {
          bullet.classList.toggle('is-active', bulletIndex === nextIndex);
          bullet.setAttribute('aria-current', bulletIndex === nextIndex ? 'true' : 'false');
        });
      }

      const shouldDisableButtons = slideCount < 2;

      if (this.prevButton) {
        this.prevButton.disabled = shouldDisableButtons;
      }

      if (this.nextButton) {
        this.nextButton.disabled = shouldDisableButtons;
      }

      if (shouldFocus && this.viewport) {
        this.viewport.focus({ preventScroll: true });
      }

      // Reset the autoplay clock so a full interval elapses
      // after any navigation (manual or automatic)
      this.lastAdvanceTime = Date.now();
    }
  }

  customElements.define('testimonial-split-slider', TestimonialSplitSlider);
}